import type { ArtifactContent } from "../ChatEventTypes.ts";
import type { SpeakingBlockState } from "../chat-panel/chatPanelState.ts";

export type SpeakingMarkdownSegment = {
	kind: "markdown";
	key: string;
	markdown: string;
};

export type SpeakingArtifactSegment = {
	kind: "artifact";
	key: string;
	id: string;
	artifact: ArtifactContent;
};

export type SpeakingBlockSegment =
	| SpeakingMarkdownSegment
	| SpeakingArtifactSegment;

function artifactPlaceholderIds(block: SpeakingBlockState): string[] {
	return Object.entries(block.mentions)
		.filter(([, entry]) => entry.kind === "artifact")
		.map(([id]) => id);
}

type PlaceholderPosition = { index: number; id: string; length: number };

function rangesOverlap(
	a: PlaceholderPosition,
	b: PlaceholderPosition,
): boolean {
	return a.index < b.index + b.length && b.index < a.index + a.length;
}

/**
 * Client-side token spliced into speaking markdown for a mention. The trailing
 * `-` stops `GVTMPH-…-1` matching as a prefix of `GVTMPH-…-10`.
 */
export function mentionPlaceholderId(scope: string, counter: number): string {
	return `GVTMPH-${scope}-${counter}-`;
}

/** Find exact placeholder ids in text, preferring longer ids when prefixes overlap. */
export function findPlaceholderPositions(
	text: string,
	ids: string[],
): PlaceholderPosition[] {
	const sortedIds = [...ids].sort((a, b) => b.length - a.length);
	const positions: PlaceholderPosition[] = [];

	for (const id of sortedIds) {
		if (id.length === 0) {
			continue;
		}

		let searchFrom = 0;
		while (searchFrom < text.length) {
			const index = text.indexOf(id, searchFrom);
			if (index === -1) {
				break;
			}

			const candidate: PlaceholderPosition = { index, id, length: id.length };
			if (!positions.some((existing) => rangesOverlap(existing, candidate))) {
				positions.push(candidate);
			}

			searchFrom = index + 1;
		}
	}

	positions.sort((a, b) => a.index - b.index);
	return positions;
}

/**
 * Split a speaking block into markdown runs and artifact segments. Artifacts are
 * rendered as Solid components in the chat tree; inline mention placeholders stay
 * inside markdown segments for DOM hydration.
 */
export function parseSpeakingBlockSegments(
	block: SpeakingBlockState,
): SpeakingBlockSegment[] {
	const artifactIds = artifactPlaceholderIds(block);
	if (artifactIds.length === 0) {
		if (block.markdown.length === 0) {
			return [];
		}
		return [{ kind: "markdown", key: "md:only", markdown: block.markdown }];
	}

	const positions = findPlaceholderPositions(block.markdown, artifactIds);
	if (positions.length === 0) {
		return [{ kind: "markdown", key: "md:start", markdown: block.markdown }];
	}

	const segments: SpeakingBlockSegment[] = [];
	let cursor = 0;
	let lastArtifactId: string | undefined;

	for (const position of positions) {
		if (position.index > cursor) {
			segments.push({
				kind: "markdown",
				key: lastArtifactId ? `md:after:${lastArtifactId}` : "md:start",
				markdown: block.markdown.slice(cursor, position.index),
			});
		}

		const entry = block.mentions[position.id];
		if (entry?.kind === "artifact") {
			segments.push({
				kind: "artifact",
				key: `artifact:${position.id}`,
				id: position.id,
				artifact: entry.data,
			});
			lastArtifactId = position.id;
		}

		cursor = position.index + position.length;
	}

	if (cursor < block.markdown.length) {
		segments.push({
			kind: "markdown",
			key: `md:after:${lastArtifactId ?? "start"}`,
			markdown: block.markdown.slice(cursor),
		});
	}

	return segments;
}

/**
 * Reuse segment object identities across parses so `<For>` keeps mounted views
 * while streaming updates mutate markdown/artifact payload in place.
 */
export function reconcileSpeakingBlockSegments(
	previous: SpeakingBlockSegment[],
	next: SpeakingBlockSegment[],
): SpeakingBlockSegment[] {
	const previousByKey = new Map(
		previous.map((segment) => [segment.key, segment]),
	);
	const reconciled: SpeakingBlockSegment[] = [];

	for (const segment of next) {
		const existing = previousByKey.get(segment.key);
		if (!existing || existing.kind !== segment.kind) {
			reconciled.push(segment);
			continue;
		}

		if (segment.kind === "markdown" && existing.kind === "markdown") {
			existing.markdown = segment.markdown;
			reconciled.push(existing);
			continue;
		}

		if (segment.kind === "artifact" && existing.kind === "artifact") {
			existing.id = segment.id;
			existing.artifact = segment.artifact;
			reconciled.push(existing);
		}
	}

	return reconciled;
}

export function markdownForSpeakingSegment(
	block: SpeakingBlockState,
	segmentKey: string,
): string {
	for (const segment of parseSpeakingBlockSegments(block)) {
		if (segment.kind === "markdown" && segment.key === segmentKey) {
			return segment.markdown;
		}
	}
	return "";
}
